/* eslint-disable react/forbid-prop-types */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Chip from '@material-ui/core/Chip';
import Box from '@material-ui/core/Box';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@material-ui/icons/Close';
import { useSetRecoilState } from 'recoil';
import DialogCloseButton from '~/src/components/DialogCloseButton';
import useApiClient from '~/src/hooks/useApiClient';
import { snackbarMessageSelector } from '~/src/Snackbar/store';

const TAG_NAME_PATTERN = /^[A-Za-z0-9- ]+$/;

const useStyles = makeStyles(({ spacing }) => ({
  closeFullWidth: {
    justifyContent: 'flex-start',
  },
  content: {
    minWidth: spacing(80),
    maxHeight: '60vh',
    overflow: 'auto',
  },
  tagGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing(1),
    marginBottom: spacing(2),
  },
  newTagField: {
    marginTop: spacing(2),
    display: 'flex',
    alignItems: 'flex-start',
    '& > * + *': {
      marginLeft: spacing(1),
    },
  },
}));

interface EditTagsDialogProps {
  projectId: number;
  projectName: string;
  open: boolean;
  onClose: () => void;
  onTagsChanged?: (tags: string[]) => void;
}

const EditTagsDialog = ({
  projectId,
  projectName,
  open,
  onClose,
  onTagsChanged,
}: EditTagsDialogProps) => {
  const { t } = useTranslation('projects');
  const classes = useStyles();
  const apiClient = useApiClient();
  const setSnackbar = useSetRecoilState(snackbarMessageSelector);

  const [projectTags, setProjectTags] = useState<string[]>([]);
  const [newTagName, setNewTagName] = useState('');

  const loadTags = async () => {
    try {
      const response = await apiClient.get<string[]>(`/projects/${projectId}/tags`);
      const tags = response.data || [];
      setProjectTags(tags);
      return tags;
    } catch (err) {
      return null;
    }
  };

  useEffect(() => {
    if (open) {
      setNewTagName('');
      loadTags();
    }
  }, [open, projectId]);

  const handleRemoveTag = async (tagName: string) => {
    try {
      await apiClient.delete(`/projects/${projectId}/tags/${encodeURIComponent(tagName)}`);
      const refreshed = await loadTags();
      if (refreshed && onTagsChanged) onTagsChanged(refreshed);
    } catch {
      setSnackbar({ message: 'Failed to remove tag', severity: 'error' });
    }
  };

  const trimmedTagName = newTagName.trim();
  const isTagNameInvalid = trimmedTagName.length > 0 && !TAG_NAME_PATTERN.test(trimmedTagName);

  const handleAddNewTag = async () => {
    const name = newTagName.trim();
    if (!name || name.length > 20 || projectTags.length >= 5) return;
    if (!TAG_NAME_PATTERN.test(name)) return;

    if (projectTags.some((tag) => tag.toLowerCase() === name.toLowerCase())) {
      setNewTagName('');
      return;
    }

    try {
      await apiClient.post(`/projects/${projectId}/tags`, {
        tags: [name],
      });
      setNewTagName('');
      const refreshed = await loadTags();
      if (refreshed && onTagsChanged) onTagsChanged(refreshed);
    } catch {
      setSnackbar({ message: 'Failed to add tag', severity: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle classes={{ root: classes.closeFullWidth }}>
        {t('editTagsDialog.title', { name: projectName })}
        <DialogCloseButton onClick={onClose} />
      </DialogTitle>
      <DialogContent className={classes.content} dividers>
        <Box className={classes.tagGrid}>
          {projectTags.map((tagName) => (
            <Chip
              key={tagName}
              label={tagName}
              color="primary"
              variant="filled"
              onDelete={() => handleRemoveTag(tagName)}
              deleteIcon={<CloseIcon />}
              size="medium"
            />
          ))}
        </Box>

        <Box className={classes.newTagField}>
          <TextField
            fullWidth
            placeholder={t('editTagsDialog.newTagPlaceholder')}
            value={newTagName}
            onChange={(e) => setNewTagName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddNewTag();
              }
            }}
            inputProps={{ maxLength: 20 }}
            error={isTagNameInvalid}
            helperText={
              isTagNameInvalid
                ? t('editTagsDialog.invalidTag')
                : t('editTagsDialog.tagAllowedCharacters')
            }
          />
          <Button
            variant="outlined"
            onClick={handleAddNewTag}
            disabled={
              !newTagName.trim()
              || newTagName.trim().length > 20
              || projectTags.length >= 5
              || isTagNameInvalid
            }
          >
            {t('editTagsDialog.add')}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

EditTagsDialog.propTypes = {
  projectId: PropTypes.number.isRequired,
  projectName: PropTypes.string.isRequired,
  currentTags: PropTypes.arrayOf(PropTypes.string),
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onTagsChanged: PropTypes.func,
};

export default EditTagsDialog;
